import { useCallback, useState } from 'react';

import { AsyncListUIState } from './types';

const INITIAL_STATE: AsyncListUIState = {
  includeIds: [],
  excludeIds: [],
  isSelectAll: false,
  userGroupIds: [],
};

export default function useAsyncListActions(initalState?: AsyncListUIState) {
  const [uiState, setUIState] = useState<AsyncListUIState>(initalState ?? INITIAL_STATE);

  const onReset = useCallback(() => {
    setUIState(INITIAL_STATE);
  }, []);

  const onSelectAllChange = useCallback(() => {
    setUIState((prevState) => ({
      isSelectAll: !prevState.isSelectAll,
      includeIds: [],
      excludeIds: [],
      userGroupIds: [],
    }));
  }, []);

  const onChange = useCallback(
    (listItemId: string, checked: boolean, userGroupId = '', isInGroups = false) => {
      if (userGroupId && !listItemId && checked) {
        if (uiState?.userGroupIds?.find((id) => id == userGroupId)) return;
      }
      setUIState(({ includeIds, excludeIds, isSelectAll, userGroupIds = [] }) => {
        let updatedIncludedIds: string[] = [];
        let updatedExcludedIds: string[] = [];
        let updatedUserGroupIds: string[] = [];

        if (!Boolean(listItemId.length) && userGroupId) {
          if (checked) {
            updatedUserGroupIds = [...userGroupIds, userGroupId];
          } else {
            updatedUserGroupIds = [...userGroupIds.filter((id) => id != userGroupId)];
          }
          return {
            includeIds,
            excludeIds,
            isSelectAll,
            userGroupIds: updatedUserGroupIds,
          };
        }

        if (checked) {
          updatedIncludedIds = [...includeIds, listItemId];
          updatedExcludedIds = excludeIds.filter((id) => id !== listItemId);
        } else {
          if (!!includeIds?.find((id) => id === listItemId)) {
            updatedIncludedIds = includeIds.filter((id) => id !== listItemId);
            updatedExcludedIds = excludeIds;
          } else {
            updatedIncludedIds = includeIds;
            updatedExcludedIds = [...excludeIds, listItemId];
          }
        }
        if (isInGroups) {
          if (checked) {
            updatedIncludedIds = includeIds.filter((id) => id != listItemId);
            updatedExcludedIds = excludeIds.filter((id) => id !== listItemId);
          } else {
            updatedExcludedIds = [...excludeIds.filter((id) => id !== listItemId), listItemId];
          }
        }
        return {
          includeIds: updatedIncludedIds,
          excludeIds: updatedExcludedIds,
          isSelectAll,
          userGroupIds: userGroupIds,
        };
      });
    },
    [uiState?.userGroupIds],
  );

  const isItemSelected = useCallback(
    (listItemId: string, isInGroups?: boolean) => {
      const { includeIds, excludeIds, isSelectAll } = uiState;
      if (isInGroups && !excludeIds.find((id) => listItemId === id)) {
        return true;
      }
      if (includeIds.find((id) => listItemId === id)) {
        return true;
      }

      if (excludeIds.find((id) => listItemId === id)) {
        return false;
      }

      if (isSelectAll) {
        return true;
      }

      return false;
    },
    [uiState],
  );

  const getSelectedCount = useCallback(
    (totalCount?: number | null) => {
      const { isSelectAll, excludeIds, includeIds } = uiState;

      if (isSelectAll) {
        return (totalCount || 0) - excludeIds.length;
      }

      return includeIds.length;
    },
    [uiState],
  );

  return {
    state: {
      includeIds: uiState.includeIds,
      excludeIds: uiState.excludeIds,
      isSelectAll: uiState.isSelectAll,
      isPartiallySelected: uiState.includeIds.length !== 0 || uiState.excludeIds.length !== 0,
      userGroupIds: uiState.userGroupIds,
    },
    methods: {
      isItemSelected,
      onChange,
      onSelectAllChange,
      onReset,
      getSelectedCount,
    },
  };
}
